/**
 * SEO utilities for managing meta tags across the website
 */

interface MetaTagsProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  language?: string;
}

/**
 * Updates the meta tags in the document head based on provided values
 */
export const updateMetaTags = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage = "https://lovable.dev/opengraph-image-p98pqg.png", // Default OG image
  ogType = "website",
  language = "de", // Default language
}: MetaTagsProps): void => {
  // Basic meta tags
  document.title = title;
  
  // Update description
  let metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute('content', description);
  } else {
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    metaDescription.setAttribute('content', description);
    document.head.appendChild(metaDescription);
  }

  // Update keywords if provided
  if (keywords) {
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', keywords);
    } else {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      metaKeywords.setAttribute('content', keywords);
      document.head.appendChild(metaKeywords);
    }
  }

  // Add language alternate links for SEO
  updateLanguageAlternateLinks(canonicalUrl, language);

  // Update language attribute on html tag
  document.documentElement.setAttribute('lang', language);

  // Update canonical URL if provided
  if (canonicalUrl) {
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', canonicalUrl);
    } else {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      canonicalLink.setAttribute('href', canonicalUrl);
      document.head.appendChild(canonicalLink);
    }
  }

  // Update Open Graph tags
  updateOpenGraphTag('og:title', title);
  updateOpenGraphTag('og:description', description);
  updateOpenGraphTag('og:image', ogImage);
  updateOpenGraphTag('og:type', ogType);
  if (canonicalUrl) {
    updateOpenGraphTag('og:url', canonicalUrl);
  }
  
  // Add locale OG tag based on language
  updateOpenGraphTag('og:locale', language === 'de' ? 'de_CH' : 
                               language === 'fr' ? 'fr_CH' : 
                               language === 'it' ? 'it_CH' : 'en_CH');

  // Update Twitter tags
  updateTwitterTag('twitter:title', title);
  updateTwitterTag('twitter:description', description);
  updateTwitterTag('twitter:image', ogImage);
  updateTwitterTag('twitter:card', 'summary_large_image');
};

/**
 * Helper function to add language alternate links for multilingual SEO
 */
const updateLanguageAlternateLinks = (baseUrl?: string, currentLang?: string): void => {
  if (!baseUrl) return;
  
  // Remove existing alternate links
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
  
  // Extract the base URL without language
  const urlParts = baseUrl.split('/');
  let baseUrlNoLang = '';
  
  if (urlParts.length >= 4) {
    // Remove the language part and reconstruct
    urlParts.splice(3, 1);
    baseUrlNoLang = urlParts.join('/');
  } else {
    baseUrlNoLang = baseUrl.replace(/\/(de|en|fr|it)($|\/)/, '/');
  }
  
  // Add alternate links for each language
  const languages = ['de', 'en', 'fr', 'it'];
  languages.forEach(lang => {
    // Skip adding the link for the current language
    if (lang === currentLang) return;
    
    const hrefLangUrl = baseUrlNoLang.replace(/^(https?:\/\/[^\/]+)\//, `$1/${lang}/`);
    const link = document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', lang);
    link.setAttribute('href', hrefLangUrl);
    document.head.appendChild(link);
  });
};

/**
 * Helper function to update or create Open Graph meta tags
 */
const updateOpenGraphTag = (property: string, content: string): void => {
  let tag = document.querySelector(`meta[property="${property}"]`);
  if (tag) {
    tag.setAttribute('content', content);
  } else {
    tag = document.createElement('meta');
    tag.setAttribute('property', property);
    tag.setAttribute('content', content);
    document.head.appendChild(tag);
  }
};

/**
 * Helper function to update or create Twitter meta tags
 */
const updateTwitterTag = (name: string, content: string): void => {
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (tag) {
    tag.setAttribute('content', content);
  } else {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    tag.setAttribute('content', content);
    document.head.appendChild(tag);
  }
};

/**
 * Generates a canonical URL for the current page
 */
export const getCanonicalUrl = (path: string, language: string): string => {
  const baseUrl = 'https://future-level.ch';
  return `${baseUrl}/${language}${path ? `/${path}` : ''}`;
};

/**
 * Generates structured data markup for blog articles 
 */
export const generateArticleSchema = (
  title: string,
  description: string, 
  imageUrl: string,
  publishDate: string,
  canonicalUrl: string,
  language: string,
  keywords: string
): void => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": imageUrl,
    "datePublished": formatSchemaDate(publishDate),
    "dateModified": formatSchemaDate(publishDate),
    "author": {
      "@type": "Organization",
      "name": "Future Level",
      "url": "https://future-level.ch"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Future Level",
      "logo": {
        "@type": "ImageObject",
        "url": "https://future-level.ch/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    },
    "inLanguage": language,
    "keywords": keywords
  };
  
  let scriptTag = document.getElementById('article-schema') as HTMLScriptElement;
  
  if (!scriptTag) {
    scriptTag = document.createElement('script');
    scriptTag.id = 'article-schema';
    scriptTag.setAttribute('type', 'application/ld+json');
    document.head.appendChild(scriptTag);
  }
  
  scriptTag.textContent = JSON.stringify(schema);
};

/**
 * Format date for schema.org (YYYY-MM-DD)
 */
const formatSchemaDate = (dateString: string): string => {
  // Convert from DD.MM.YYYY to YYYY-MM-DD
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateString)) {
    const parts = dateString.split('.');
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return dateString;
};
